import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Select, Button, message, Checkbox, Tooltip, Row, Col } from 'antd'
import { api } from '../../../services/index.js'

const { Option } = Select

const EditGroupModal = ({
    visible,
    onCancel,
    onSuccess,
    group,
    locationId,
    users,
    userId,
    pain_point,
    include_mc,
    include_open_ended,
    include_scenario
}) => {
    const [isUpdating, setIsUpdating] = useState(false)
    const [formData, setFormData] = useState({
        group_name: '',
        user_ids: [],
        pain_point: '',
        include_mc: true,
        include_open_ended: true,
        include_scenario: false
    })

    useEffect(() => {
        if (group) {
            setFormData({
                group_name: group.group_name || '',
                user_ids: group.users?.map(u => Number(u.user_id || u.id)) || [],
                pain_point: pain_point || '',
                include_mc: include_mc !== undefined ? include_mc : true,
                include_open_ended: include_open_ended !== undefined ? include_open_ended : true,
                include_scenario: include_scenario !== undefined ? include_scenario : false
            })
        }
    }, [group, pain_point, include_mc, include_open_ended, include_scenario])

    const handleUserChange = async (value) => {
        const prevIds = formData.user_ids || []
        if (value.length > 5 && value.length > prevIds.length) {
            message.warning('You can select a maximum of 5 users')
            return
        }
        const nextIds = value.map(Number)

        const removed = prevIds.filter(id => !nextIds.includes(id))

        for (const userId of removed) {
            try {
                await api.quest.removeUserFromGroup(locationId, group.group_id, userId)
            } catch (error) {
                console.error('Error removing user:', error)
                message.error('Failed to remove user')
            }
        }

        setFormData({ ...formData, user_ids: nextIds })
    }

    const handleUpdate = async () => {
        if (!formData.group_name) {
            message.error('Please input group name')
            return
        }
        if (!formData.user_ids.length) {
            message.error('Please select at least one user')
            return
        }

        setIsUpdating(true)
        try {
            const payload = {
                group_name: formData.group_name,
                user_ids: formData.user_ids.map(String),
                assigned_by: String(userId)
            }
            const res = await api.quest.updateLocationGroup(locationId, group.group_id, payload)
            const hasLocationDataChanged = formData.pain_point !== pain_point ||
                formData.include_mc !== include_mc ||
                formData.include_open_ended !== include_open_ended ||
                formData.include_scenario !== include_scenario;

            if (hasLocationDataChanged) {
                try {
                    await api.quest.updateSpecificLocation(locationId, {
                        pain_point: formData.pain_point,
                        include_mc: formData.include_mc,
                        include_open_ended: formData.include_open_ended,
                        include_scenario: formData.include_scenario
                    })
                } catch (error) {
                    console.error('Error updating location settings:', error)
                    message.error('Failed to update location settings')
                }
            }

            if (res?.data) {
                message.success('Group updated successfully')
                if (onSuccess) {
                    onSuccess(res.data)
                }
            }
        } catch (error) {
            console.error('Error updating group:', error)
            message.error('Failed to update group')
        } finally {
            setIsUpdating(false)
        }
    }

    return (
        <Modal
            title="Edit Group"
            visible={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" shape="round" type="default" style={{ borderRadius: '50px' }} disabled={isUpdating} onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    style={{ borderRadius: '50px' }}
                    shape="round"
                    key="update"
                    type="primary"
                    loading={isUpdating}
                    onClick={handleUpdate}
                >
                    Update
                </Button>
            ]}
            centered
        >
            <Form layout="vertical">
                <Form.Item label="Group Name" required>
                    <Input
                        placeholder="e.g. Group A"
                        value={formData.group_name}
                        onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                    />
                </Form.Item>
                <Form.Item label="Select Users" required>
                    <Select
                        mode="multiple"
                        placeholder="Select Users"
                        value={formData.user_ids}
                        onChange={handleUserChange}
                        showSearch
                        optionLabelProp="label"
                        filterOption={(input, option) => {
                            const nameMatch = (option.props.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            const emailMatch = (option.props['data-email'] || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            return nameMatch || emailMatch;
                        }}
                    >
                        {users.map(user => {
                            const userLabel = (user.user?.first_name || user.first_name)
                                ? `${user.user?.first_name || user.first_name || ''} ${user.user?.last_name || user.last_name || ''}`.trim()
                                : (user.user?.email || user.email || user.user?.username || user.username || String(user.user_id));
                            return (
                                <Option key={user.user_id} value={Number(user.user_id)} label={userLabel} data-email={user.user?.email}>
                                    {userLabel}
                                </Option>
                            )
                        })}
                    </Select>
                </Form.Item>
                <Form.Item label="Pain Point">
                    <Input.TextArea
                        placeholder="e.g. Pain Point description..."
                        value={formData.pain_point}
                        onChange={(e) => setFormData({ ...formData, pain_point: e.target.value })}
                        rows={3}
                    />
                </Form.Item>
                <Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Tooltip title="This will enable/disable multiple choice questions">
                                <Checkbox
                                    checked={formData.include_mc}
                                    onChange={(e) => setFormData({ ...formData, include_mc: e.target.checked })}
                                >
                                    Include MCQ
                                </Checkbox>
                            </Tooltip>
                        </Col>
                        <Col span={12}>
                            <Tooltip title="This will enable/disable open ended question">
                                <Checkbox
                                    checked={formData.include_open_ended}
                                    onChange={(e) => setFormData({ ...formData, include_open_ended: e.target.checked })}
                                >
                                    Include Open Ended
                                </Checkbox>
                            </Tooltip>
                        </Col>
                        <Col span={12} style={{ marginTop: '10px' }}>
                            <Tooltip title="This will enable/disable scenario questions">
                                <Checkbox
                                    checked={formData.include_scenario}
                                    onChange={(e) => setFormData({ ...formData, include_scenario: e.target.checked })}
                                >
                                    Include Scenario
                                </Checkbox>
                            </Tooltip>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default EditGroupModal
